/**
 * R5 创建页草稿(2026-08-08)—— 纯前端 localStorage,不进后端。
 *
 * ═══ 存什么、不存什么 ═════════════════════════════════════════════════════════
 * 存:文案、音色来源与音色、语速、档位、清晰度、形象**来源与引用**(仅 preset/profile)。
 * ★**不存上传照片/视频的临时 key**。那个 key 是 `uploads/avatar` 返的一次性对象键,
 *   过期即死链 —— 存下来只会在恢复时给用户一个「看着像选好了、一出片就报错」的形象。
 *   这类「存了个会 404 的引用」的坑,详情页「回工作台改这稿」已经踩过一次并明确不带
 *   (见 `dhRemake.ts` 头注)。这里同一条规矩:**只记「你上次传过一张照片」这个事实**
 *   (`hadUpload`),恢复时明说要重传,不拿一个死引用骗人。
 *
 * ═══ key 按「租户 + 用户 + 线」分 ═══════════════════════════════════════════
 * 三条线是三条真·不同的管线(引擎/参数集/计费口径都不同),草稿混在一起等于把
 * 配音稿恢复到口播工作台。租户 + 用户是因为这台电脑可能换人登录 —— 别把上一个人的
 * 稿子恢复给下一个人看(分身线的文案往往是客户话术)。
 *
 * ═══ 什么时候清 ═══════════════════════════════════════════════════════════
 * 出片成功 → 清(这稿已经变成任务了,留着只会在下次进页时勾起一份已经做完的活)。
 * ★「取消」**不清** —— 取消是「我先不出了」,不是「我不要这稿了」。把取消当成丢弃,
 *   等于用户点错一下就没了。真要丢,恢复条上有明确的「丢掉」出口。
 */

import {
  DH_DEFAULT_VOICE_LANGUAGE,
  DH_DEFAULT_VOICE_STYLE,
  isKnownVoiceDialect,
  isKnownVoiceLanguage,
  isKnownVoiceStyle,
} from "@/entities/dh-profile/model/voiceV2Options";

export const DH_DRAFT_VERSION = 2;

export interface DhDraft {
  v: number;
  script: string;
  mode: string;
  resolution: string;
  voiceKind: "preset" | "profile";
  voice: string;
  profileId: string;
  speechRate: number;
  /** 表演风格(仅生动版生效;老草稿无键 → 恢复为默认 professional,不整稿作废)。 */
  performanceStyle: string;
  /**
   * 口播线「用原声 / 换音色」。v1 不存它,恢复后总是回到默认 true,用户选好的
   * 声纹被静默丢弃(音色控件整块藏起,提交时 voiceFieldsOf 直接回 preset)。
   */
  useOriginalVoice: boolean;
  /**
   * 声音 V2 三件套(2026-09-08)。老草稿无键 → 恢复为默认(zh / null=普通话 / natural),
   * 不整稿作废 —— 与 performanceStyle 同一先例。词表外的值同样退默认(词表会演进,
   * 别让一个下架的方言把整份稿废掉)。
   */
  voiceLanguage: string;
  voiceDialect: string | null;
  voiceStyle: string;
  /** 字幕开关(0908,可缺省=true:老草稿没这个键不许把人家的字幕关掉)。 */
  burnSubtitles?: boolean;
  /** 口播线逐句「这句切产品画面」,按句文本作 key(与 effectiveLines 同源)。 */
  brollOn: Record<string, boolean>;
  /** 仅 preset / profile 两种可复原的来源;upload 不存(见头注)。 */
  avatarKind: "preset" | "profile" | null;
  avatarRef: string;
  /** 上次是不是走的「上传照片/视频」口 —— 只记事实,不记那个会过期的 key。 */
  hadUpload: boolean;
  savedAt: string;
}

/**
 * 草稿身份 = 已登录用户(`xbgeo-auth`,AuthContext 写的那份)。
 *
 * ★为什么不 `useAuth()`:那会把「一个可有可无的草稿位」变成**整页的 Provider 依赖** ——
 *   `DigitalHumanCreatePage` 现有的 6 个测试文件全都是裸挂组件的,一引 hook 就集体炸
 *   `useAuth must be used within AuthProvider`(实测 77 条转红)。为一个锦上添花的功能
 *   让主页面多一条硬依赖,不划算。这里只读同一份 localStorage,读不到就退化成匿名位。
 */
export function readDraftIdentity(): { tenantCode: string | null; userId: number | null } {
  try {
    const raw = localStorage.getItem("xbgeo-auth");
    if (!raw) return { tenantCode: null, userId: null };
    const u = JSON.parse(raw) as { tenantCode?: unknown; userId?: unknown };
    return {
      tenantCode: typeof u.tenantCode === "string" ? u.tenantCode : null,
      userId: typeof u.userId === "number" ? u.userId : null,
    };
  } catch {
    return { tenantCode: null, userId: null };
  }
}

/** 草稿位。★线要进 key:三条线是三条不同管线,混着恢复就是把配音稿铺到口播工作台。 */
export function dhDraftKey(
  tenantCode: string | null | undefined,
  userId: number | null | undefined,
  line: string
): string {
  return `xbgeo-dh-draft:${tenantCode ?? "-"}:${userId ?? "-"}:${line}`;
}

/** 「这份稿子值得存吗」= 用户真写了字。只改了档位/语速不算 —— 那是默认值的微调,不是稿。 */
export function isDraftWorthSaving(d: Pick<DhDraft, "script">): boolean {
  return d.script.trim().length > 0;
}

export function saveDhDraft(key: string, draft: Omit<DhDraft, "v" | "savedAt">): void {
  if (!isDraftWorthSaving(draft)) return;
  try {
    const payload: DhDraft = { ...draft, v: DH_DRAFT_VERSION, savedAt: new Date().toISOString() };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* 配额满 / 隐私模式:草稿是锦上添花,不阻断出片 */
  }
}

/**
 * 读草稿。**逐字段验型,形状不对整包丢弃** —— 与 `parseScenePreset` 同一条规矩:
 * 半信半疑地用一半,会拼出一份用户从没填过的稿子。
 * 版本不符也整包丢:草稿是可再生的临时物,不值得为它写迁移。
 */
export function loadDhDraft(key: string): DhDraft | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let o: Record<string, unknown>;
  try {
    o = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!o || typeof o !== "object" || o.v !== DH_DRAFT_VERSION) return null;
  if (typeof o.script !== "string" || !o.script.trim()) return null;
  if (typeof o.mode !== "string" || typeof o.resolution !== "string") return null;
  if (o.voiceKind !== "preset" && o.voiceKind !== "profile") return null;
  if (typeof o.voice !== "string" || typeof o.profileId !== "string") return null;
  if (typeof o.speechRate !== "number" || !Number.isFinite(o.speechRate)) return null;
  if (o.avatarKind !== null && o.avatarKind !== "preset" && o.avatarKind !== "profile") return null;
  if (typeof o.avatarRef !== "string") return null;
  if (typeof o.savedAt !== "string") return null;
  if (typeof o.useOriginalVoice !== "boolean") return null;
  const brollOn = o.brollOn;
  if (!brollOn || typeof brollOn !== "object" || Array.isArray(brollOn)) return null;
  if (Object.values(brollOn).some((x) => typeof x !== "boolean")) return null;
  return {
    v: DH_DRAFT_VERSION,
    script: o.script,
    mode: o.mode,
    resolution: o.resolution,
    voiceKind: o.voiceKind,
    voice: o.voice,
    profileId: o.profileId,
    speechRate: o.speechRate,
    performanceStyle: typeof o.performanceStyle === "string" ? o.performanceStyle : "professional",
    useOriginalVoice: o.useOriginalVoice,
    voiceLanguage: isKnownVoiceLanguage(o.voiceLanguage)
      ? o.voiceLanguage
      : DH_DEFAULT_VOICE_LANGUAGE,
    // 方言依附语言:语言不是中文时一律回普通话位(契约「仅中文可非空」,存量稿也照此收口)。
    voiceDialect:
      isKnownVoiceLanguage(o.voiceLanguage) &&
      o.voiceLanguage === "zh" &&
      isKnownVoiceDialect(o.voiceDialect)
        ? o.voiceDialect
        : null,
    voiceStyle: isKnownVoiceStyle(o.voiceStyle) ? o.voiceStyle : DH_DEFAULT_VOICE_STYLE,
    burnSubtitles: typeof o.burnSubtitles === "boolean" ? o.burnSubtitles : true,
    brollOn: brollOn as Record<string, boolean>,
    avatarKind: o.avatarKind,
    avatarRef: o.avatarRef,
    hadUpload: o.hadUpload === true,
    savedAt: o.savedAt,
  };
}

export function clearDhDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** 「N 分钟前 / N 小时前 / M 月D日」—— 恢复条上要说清这是**什么时候**的稿。 */
export function draftAgeLabel(savedAt: string, now = Date.now()): string {
  const t = Date.parse(savedAt);
  if (!Number.isFinite(t)) return "";
  const min = Math.floor((now - t) / 60000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} 小时前`;
  const d = new Date(t);
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}
